import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import LIBRARIES, { LIBRARY_ORDER } from "@/lib/libraryConfig";
import { Separator } from "@/components/ui/separator";
import { Ship, LayoutDashboard, BookOpen, Palette, DatabaseBackup, Printer } from "lucide-react";

const NAV_ITEMS = [
  { to: "/voyages", label: "Voyages", icon: Ship, testId: "nav-voyages" },
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
];

const TOOLS_ITEMS = [
  // Phase 11 — multi-library station handout PDF.
  { to: "/print/stations", label: "Print Stations", icon: Printer, testId: "nav-print-stations" },
];

const SETTINGS_ITEMS = [
  { to: "/settings/pdf-layout", label: "PDF Layout", icon: Palette, testId: "nav-settings-pdf-layout" },
  // Phase 10 — Backup & Restore.
  { to: "/settings/backup", label: "Backup & Restore", icon: DatabaseBackup, testId: "nav-settings-backup" },
];

export default function Sidebar() {
  const { pathname } = useLocation();

  const isActive = (to) => {
    if (to === "/voyages") return pathname === "/voyages" || pathname.startsWith("/voyages/");
    return pathname === to;
  };

  return (
    <aside
      className="flex h-full w-60 shrink-0 flex-col border-r bg-card"
      data-testid="sidebar"
    >
      {/* Brand */}
      <div className="flex h-14 items-center gap-2 px-4">
        <BookOpen className="h-5 w-5 text-primary" />
        <span className="text-base font-bold tracking-tight text-foreground" data-testid="app-title">
          VoyageMenu
        </span>
      </div>

      <Separator />

      {/* Primary nav */}
      <nav className="flex flex-col gap-0.5 px-2 py-3" data-testid="nav-primary">
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.to);
          return (
            <Link
              key={item.to}
              to={item.to}
              data-testid={item.testId}
              className={cn(
                "flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                "hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                active
                  ? "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
                  : "text-muted-foreground"
              )}
            >
              <Icon className="h-4 w-4" />
              {item.label}
            </Link>
          );
        })}
      </nav>

      <Separator />

      {/* Settings nav — Phase 8 onwards */}
      <div className="px-3 pt-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Settings
        </span>
      </div>
      <nav className="flex flex-col gap-0.5 px-2 py-2" data-testid="nav-settings">
        {SETTINGS_ITEMS.map((item) => {
          const Icon = item.icon;
          const active = pathname === item.to;
          return (
            <Link
              key={item.to}
              to={item.to}
              data-testid={item.testId}
              className={cn(
                "flex items-center gap-2.5 rounded-md px-3 py-1.5 text-sm transition-colors",
                "hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                active
                  ? "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
                  : "text-muted-foreground"
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              {item.label}
            </Link>
          );
        })}
      </nav>

      <Separator />

      {/* Tools nav — Phase 11 onwards */}
      <div className="px-3 pt-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Tools
        </span>
      </div>
      <nav className="flex flex-col gap-0.5 px-2 py-2" data-testid="nav-tools">
        {TOOLS_ITEMS.map((item) => {
          const Icon = item.icon;
          const active = pathname === item.to;
          return (
            <Link
              key={item.to}
              to={item.to}
              data-testid={item.testId}
              className={cn(
                "flex items-center gap-2.5 rounded-md px-3 py-1.5 text-sm transition-colors",
                "hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                active
                  ? "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
                  : "text-muted-foreground"
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              {item.label}
            </Link>
          );
        })}
      </nav>

      <Separator />

      {/* Library nav */}
      <div className="px-3 py-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Libraries
        </span>
      </div>
      <nav
        className="flex flex-1 flex-col gap-0.5 overflow-y-auto px-2 pb-4"
        data-testid="nav-libraries"
      >
        {LIBRARY_ORDER.map((key) => {
          const lib = LIBRARIES[key];
          const Icon = lib.icon;
          const to = `/library/${key}`;
          const active = pathname === to;
          return (
            <Link
              key={key}
              to={to}
              data-testid={`nav-lib-${key}`}
              className={cn(
                "flex items-center gap-2.5 rounded-md px-3 py-1.5 text-sm transition-colors",
                "hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                active
                  ? "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
                  : "text-muted-foreground"
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              {lib.label}
            </Link>
          );
        })}
      </nav>

      {/* Footer — kept empty intentionally; the app is unbranded by design. */}
      <div className="border-t px-4 py-2" data-testid="sidebar-footer">
        <span className="text-[10px] text-muted-foreground/70">
          VoyageMenu
        </span>
      </div>
    </aside>
  );
}
